import { VERSION, COMMIT, BUILT_AT } from './version'

/**
 * Finding out that the app on screen is not the app that is deployed.
 *
 * An installed web app keeps its JavaScript until something makes it fetch new
 * JavaScript. For a home-screen PWA or a TWA that can be days — the
 * browser fetches a new release on navigation, and somebody who leaves the
 * app open never makes one. So the honest answer to "does it update
 * automatically" is: eventually, unpredictably, and with no way to tell.
 *
 * The build stamp is compiled into the bundle, which means a running app knows
 * what it IS and cannot know what it SHOULD BE. `version.json` is written beside
 * the bundle at build time and is the one thing the app can ask that is not
 * itself.
 *
 * Extracted from Travel Day, including the decision below, which is the part
 * people get wrong.
 */

export interface DeployedBuild {
  version: string
  commit: string
  builtAt: number
}

export async function deployedBuild(): Promise<DeployedBuild | null> {
  try {
    /*
     * Belt and braces: `no-store` for the HTTP cache, and a query for any
     * intermediary that ignores it. Served as a static asset, so this costs no
     * Worker invocation and still answers when the API is degraded.
     */
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
    if (!res.ok) return null
    const body = (await res.json()) as Partial<DeployedBuild>
    return body.commit ? { version: body.version ?? '', commit: body.commit, builtAt: body.builtAt ?? 0 } : null
  } catch {
    /*
     * Offline, or not deployed yet. Either way there is nothing to claim: an
     * update prompt shown because the network hiccuped is a prompt people learn
     * to ignore, and then the one that matters is ignored too.
     */
    return null
  }
}

/**
 * Whether what is deployed differs from what is running.
 *
 * Include the build timestamp: repeated builds from an uncommitted checkout
 * share a commit label, even when their code and version have changed. A
 * rollback also needs to be offered, so compare identities rather than age.
 */
export function isNewer(deployed: DeployedBuild | null): boolean {
  if (!deployed) return false
  // A dev build has no commit worth comparing. Never nag in development.
  if (!COMMIT || COMMIT === 'dev' || COMMIT === 'unknown') return false
  return deployed.commit !== COMMIT || deployed.version !== VERSION
    || (deployed.builtAt > 0 && deployed.builtAt !== BUILT_AT)
}

/** `v1.1.2` — what they would be moving to, when it is worth naming. */
export function deployedLabel(deployed: DeployedBuild): string {
  return deployed.version && deployed.version !== VERSION ? `v${deployed.version}` : ''
}

/** Preserve the route and shared links while requesting a fresh HTML document. */
export function refreshUrl(href: string): string {
  const url = new URL(href)
  url.searchParams.set('_refresh', String(Date.now()))
  return url.href
}

export function refreshApp(): void {
  window.location.replace(refreshUrl(window.location.href))
}
